import { ArrowDown, ArrowUp, ChevronRight, Pencil, Send } from 'lucide-react';
import { Fragment, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Broadcast } from '@/features/broadcasts/broadcasts-types';

type Props = {
  broadcasts: Broadcast[];
  isLoading: boolean;
  error: Error | null;
  sendingId?: string;
  onEdit: (broadcast: Broadcast) => void;
  onSend: (broadcast: Broadcast) => void;
};

export const BroadcastsTable = ({
  broadcasts,
  isLoading,
  error,
  sendingId,
  onEdit,
  onSend,
}: Props) => {
  const [ascending, setAscending] = useState(true);

  if (isLoading) {
    return (
      <p className="py-12 text-center text-muted-foreground">
        Loading broadcasts...
      </p>
    );
  }
  if (error) {
    return (
      <p className="rounded-md border border-destructive px-4 py-3 text-sm text-destructive">
        {error.message}
      </p>
    );
  }
  if (broadcasts.length === 0) {
    return (
      <p className="py-12 text-center text-muted-foreground">
        No broadcasts configured.
      </p>
    );
  }

  const sorted = [...broadcasts].sort(
    (left, right) =>
      left.rollout_slug.localeCompare(right.rollout_slug) * (ascending ? 1 : -1)
  );

  return (
    <Table containerClassName="rounded-md border">
      <TableHeader>
        <TableRow>
          <TableHead>Broadcast</TableHead>
          <TableHead aria-sort={ascending ? 'ascending' : 'descending'}>
            <button
              type="button"
              className="flex items-center gap-1 hover:text-foreground"
              onClick={() => setAscending((value) => !value)}
            >
              Rollout
              {ascending ? (
                <ArrowUp className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <ArrowDown className="h-3.5 w-3.5" aria-hidden />
              )}
            </button>
          </TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((broadcast) => {
          const isSending = sendingId === broadcast.id;
          return (
            <Fragment key={broadcast.id}>
              <TableRow>
                <TableCell className="max-w-xl align-top">
                  <p className="font-medium">{broadcast.title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {broadcast.section === 'production'
                      ? 'Production'
                      : 'Experiment'}
                  </p>
                </TableCell>
                <TableCell className="align-top text-xs text-muted-foreground">
                  <p className="font-mono">{broadcast.rollout_slug}</p>
                  {broadcast.flag_key !== broadcast.rollout_slug && (
                    <p className="mt-1">Flag: {broadcast.flag_key}</p>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  {broadcast.scheduled_local_at ? (
                    <>
                      <p>Scheduled</p>
                      <time
                        className="text-sm text-muted-foreground"
                        dateTime={broadcast.scheduled_local_at}
                      >
                        {broadcast.scheduled_local_at.replace('T', ' ')} in each
                        recipient&apos;s timezone
                      </time>
                    </>
                  ) : broadcast.send_started_at ? (
                    <>
                      <p>Send started</p>
                      <time
                        className="text-sm text-muted-foreground"
                        dateTime={broadcast.send_started_at}
                      >
                        {new Date(broadcast.send_started_at).toLocaleString()}
                      </time>
                    </>
                  ) : (
                    'Draft'
                  )}
                </TableCell>
                <TableCell className="align-top">
                  {!broadcast.send_started_at && (
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onSend(broadcast)}
                        disabled={isSending}
                      >
                        <Send />
                        {isSending ? 'Queuing...' : 'Send or schedule'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(broadcast)}
                        disabled={isSending}
                      >
                        <Pencil />
                        Edit
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="p-0">
                  <details className="group">
                    <summary className="flex cursor-pointer list-none items-center gap-2 px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
                      <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                      View message
                    </summary>
                    <p className="whitespace-pre-wrap bg-muted px-6 py-5 text-base leading-7 text-foreground">
                      {broadcast.text}
                    </p>
                  </details>
                </TableCell>
              </TableRow>
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};
